import { useEffect } from "react";
import { Music2, Instagram, Facebook, Play, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead } from "./Shared";

const PLATFORM_ICONS = { TikTok: Music2, Instagram, Facebook };

const PROFILES = [
  { name: "TikTok", key: "tiktok", handle: "@electrodomesticosrivera", icon: Music2, color: "#000000", desc: "Videos de nuevos ingresos, ofertas y envíos a todo Honduras." },
  { name: "Instagram", key: "instagram", handle: "@electrodomesticosrivera", icon: Instagram, color: "#E1306C", desc: "Fotos del showroom y de la mercancía recién llegada." },
  { name: "Facebook", key: "facebook", handle: "Electrodomésticos Rivera", icon: Facebook, color: "#1877F2", desc: "Noticias, promociones y atención directa a clientes." },
];

const videoId = (url) => {
  const m = url && url.match(/\/video\/(\d+)/);
  return m ? m[1] : null;
};

export default function SocialFeed({ settings, posts }) {
  useEffect(() => {
    if (!posts.some((p) => p.platform === "TikTok" && videoId(p.url))) return;
    if (document.getElementById("tiktok-embed-js")) return;
    const s = document.createElement("script");
    s.id = "tiktok-embed-js";
    s.src = "https://www.tiktok.com/embed.js";
    s.async = true;
    document.body.appendChild(s);
  }, [posts]);

  return (
    <section data-testid="social-section" className="bg-white py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHead
          num="03"
          testid="social-title"
          title="Nuevos ingresos"
          sub="Mira los electrodomésticos que van llegando a la tienda. Reproduce nuestros videos aquí mismo, sin salir de la página."
        />
        {posts.length > 0 && (
          <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 md:grid-cols-3" data-testid="social-posts-grid">
            {posts.map((post, i) => {
              const Icon = PLATFORM_ICONS[post.platform] || Play;
              const vid = post.platform === "TikTok" ? videoId(post.url) : null;
              return (
                <Reveal key={post.id || i} delay={Math.min(i * 0.06, 0.3)}>
                  {vid ? (
                    <div className="overflow-hidden border border-border bg-white" data-testid={`social-embed-${i}`}>
                      <blockquote
                        className="tiktok-embed"
                        cite={post.url}
                        data-video-id={vid}
                        style={{ maxWidth: "605px", minWidth: "280px", margin: 0 }}
                      >
                        <section />
                      </blockquote>
                    </div>
                  ) : (
                    <a
                      data-testid={`social-post-${i}`}
                      href={post.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative block aspect-[9/16] overflow-hidden border border-border bg-primary"
                    >
                      {post.thumbnail && (
                        <img
                          src={post.thumbnail}
                          alt={post.caption || `Video de ${post.platform} — Electrodomésticos Rivera`}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      )}
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                          <Play className="ml-0.5 h-6 w-6 fill-current" />
                        </span>
                      </span>
                      <div className="absolute inset-x-0 bottom-0 bg-primary/85 p-4 text-white">
                        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest">
                          <Icon className="h-4 w-4" /> {post.platform}
                        </span>
                        {post.caption && <p className="mt-2 line-clamp-2 text-sm">{post.caption}</p>}
                      </div>
                    </a>
                  )}
                </Reveal>
              );
            })}
          </div>
        )}
        <div className={`grid grid-cols-1 gap-4 sm:grid-cols-3 ${posts.length > 0 ? "mt-10" : ""}`} data-testid="social-profiles-grid">
          {PROFILES.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.08}>
              <a
                data-testid={`social-profile-${p.name.toLowerCase()}`}
                href={settings[p.key]}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block overflow-hidden border border-border bg-white p-6 transition-shadow duration-300 hover:shadow-lg"
              >
                <span className="absolute inset-x-0 top-0 h-1.5" style={{ backgroundColor: p.color }} />
                <div className="flex items-center gap-4 pt-2">
                  <img
                    src="/assets/logo.png"
                    alt="Logo de Electrodomésticos Rivera"
                    className="h-14 w-14 shrink-0 rounded-full border border-border object-contain"
                  />
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-base font-extrabold tracking-tight text-primary">
                      <p.icon className="h-4 w-4 shrink-0" style={{ color: p.color }} /> {p.name}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{p.handle}</p>
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                <span
                  data-testid={`social-follow-${p.name.toLowerCase()}`}
                  className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 text-sm font-semibold text-white transition-transform duration-300 group-hover:-translate-y-0.5"
                  style={{ backgroundColor: p.color }}
                >
                  Ver {p.name} <ArrowUpRight className="h-4 w-4" />
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
